import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Check,
  Calendar,
  MapPin,
  GraduationCap,
  Loader2,
} from "lucide-react";
import { StyledSelect } from "../../ui/StyledSelect";
import { supabase } from "../../../lib/supabase";
import {
  COUNTRIES,
  US_STATES,
  RESIDENCY_STATUS,
  VALIDATION,
} from "../../../constants/profile";
import { allMajors } from "../../../data/colleges";

interface ProfileCompletionModalProps {
  onComplete: () => Promise<boolean>;
}

interface FormData {
  // Step 1 - Required
  dateOfBirth: string;
  country: string;
  state: string;
  residencyStatus: string;
  // Step 2 - Optional
  gpa: string;
  satScore: string;
  actScore: string;
  intendedMajor: string;
}

interface FormErrors {
  dateOfBirth?: string;
  country?: string;
  residencyStatus?: string;
  gpa?: string;
  satScore?: string;
  actScore?: string;
}

// Map display values to database enum values for residency_status
// DB only accepts: "Domestic Student" | "International Student"
const residencyToDb: Record<string, string> = {
  "U.S. Citizen": "Domestic Student",
  "U.S. Permanent Resident (Green Card holder)": "Domestic Student",
  "Dual Citizen": "Domestic Student",
  "International Student": "International Student",
  "Non-U.S. Citizen (Other / Undocumented, incl. DACA)": "Domestic Student",
};

const STEPS = [
  { id: 1, title: "Personal Info", icon: Calendar },
  { id: 2, title: "Academic Info", icon: GraduationCap },
  { id: 3, title: "Review", icon: Check },
];

function ProfileCompletionModal({ onComplete }: ProfileCompletionModalProps) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    dateOfBirth: "",
    country: "",
    state: "",
    residencyStatus: "",
    gpa: "",
    satScore: "",
    actScore: "",
    intendedMajor: "Undecided",
  });
  const [errors, setErrors] = useState<FormErrors>({});

  // Date input formatting logic
  const handleDateChange = (value: string) => {
    let cleaned = value.replace(/[^\d/]/g, "");
    if (cleaned.length === 2 && !cleaned.includes("/")) cleaned += "/";
    else if (cleaned.length === 5 && cleaned.split("/").length === 2)
      cleaned += "/";
    if (cleaned.length <= 10) updateField("dateOfBirth", cleaned);
  };

  // Parse date string (MM/DD/YYYY) to Date object
  const parseDateString = (dateStr: string): Date | null => {
    const parts = dateStr.split("/");
    if (parts.length !== 3) return null;
    const month = parseInt(parts[0], 10);
    const day = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);
    if (isNaN(month) || isNaN(day) || isNaN(year)) return null;
    if (month < 1 || month > 12) return null;
    if (day < 1 || day > 31) return null;
    if (year < 1900 || year > new Date().getFullYear()) return null;
    return new Date(year, month - 1, day);
  };

  // Format date for database (YYYY-MM-DD)
  const formatDateForDb = (dateStr: string): string => {
    const date = parseDateString(dateStr);
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Validate Step 1 (Required fields)
  const validateStep1 = (): boolean => {
    const newErrors: FormErrors = {};

    // Date of birth validation
    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = "Date of birth is required";
    } else {
      const date = parseDateString(formData.dateOfBirth);
      if (!date) {
        newErrors.dateOfBirth = "Enter date in MM/DD/YYYY format";
      } else {
        const age =
          (Date.now() - date.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
        if (age < VALIDATION.MIN_AGE || age > VALIDATION.MAX_AGE) {
          newErrors.dateOfBirth = `Age must be between ${VALIDATION.MIN_AGE} and ${VALIDATION.MAX_AGE}`;
        }
      }
    }

    // Country validation
    if (!formData.country) {
      newErrors.country = "Country is required";
    }

    // Residency status validation
    if (!formData.residencyStatus) {
      newErrors.residencyStatus = "Residency status is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate Step 2 (GPA required, others optional)
  const validateStep2 = (): boolean => {
    const newErrors: FormErrors = {};

    // GPA is required
    if (!formData.gpa) {
      newErrors.gpa = "GPA is required";
    } else {
      const gpa = parseFloat(formData.gpa);
      if (isNaN(gpa) || gpa < VALIDATION.MIN_GPA || gpa > VALIDATION.MAX_GPA) {
        newErrors.gpa = `GPA must be between ${VALIDATION.MIN_GPA} and ${VALIDATION.MAX_GPA}`;
      }
    }

    // SAT validation (400 - 1600) - only if provided
    if (formData.satScore) {
      const sat = parseInt(formData.satScore, 10);
      if (isNaN(sat) || sat < VALIDATION.MIN_SAT || sat > VALIDATION.MAX_SAT) {
        newErrors.satScore = `SAT score must be between ${VALIDATION.MIN_SAT} and ${VALIDATION.MAX_SAT}`;
      }
    }

    // ACT validation (1 - 36) - only if provided
    if (formData.actScore) {
      const act = parseInt(formData.actScore, 10);
      if (isNaN(act) || act < VALIDATION.MIN_ACT || act > VALIDATION.MAX_ACT) {
        newErrors.actScore = `ACT score must be between ${VALIDATION.MIN_ACT} and ${VALIDATION.MAX_ACT}`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle next step
  const handleNext = () => {
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) return;
    setCurrentStep((prev) => Math.min(prev + 1, 3));
  };

  // Handle back step
  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  // Skip Step 2 (optional fields)
  const handleSkip = () => {
    setFormData((prev) => ({
      ...prev,
      gpa: "",
      satScore: "",
      actScore: "",
      intendedMajor: "",
    }));
    setErrors({});
    setCurrentStep(3);
  };

  // Handle form submission
  const handleComplete = useCallback(async () => {
    setIsSubmitting(true);
    try {
      // Get authenticated user
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        console.error("No authenticated user");
        return;
      }

      // Prepare data for database
      const updateData: Record<string, unknown> = {
        date_of_birth: formatDateForDb(formData.dateOfBirth),
        country: formData.country,
        state: formData.state || null,
        residency_status:
          residencyToDb[formData.residencyStatus] || formData.residencyStatus,
      };

      // Add optional fields if provided
      if (formData.gpa) {
        updateData.gpa = parseFloat(formData.gpa);
      }
      if (formData.satScore) {
        updateData.sat_score = parseInt(formData.satScore, 10);
      }
      if (formData.actScore) {
        updateData.act_score = parseInt(formData.actScore, 10);
      }
      if (formData.intendedMajor) {
        updateData.intended_major = formData.intendedMajor;
      }

      // Update profile in database
      const { error: updateError } = await supabase
        .from("students")
        .update(updateData)
        .eq("auth_user_id", user.id);

      if (updateError) {
        console.error("Error updating profile:", updateError);
        alert("Failed to complete profile. Please try again.");
        return;
      }

      // Mark profile as complete
      const success = await onComplete();
      if (success) {
        navigate("/student/profile");
      }
    } catch (err) {
      console.error("Profile completion error:", err);
      alert("Failed to complete profile. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, onComplete, navigate]);

  // Update form field
  const updateField = (field: keyof FormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  // Render progress indicator
  const renderProgress = () => (
    <div className="flex items-center justify-center gap-2 mb-8">
      {STEPS.map((step, index) => (
        <div key={step.id} className="flex items-center">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
              currentStep >= step.id
                ? "bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow-[0_0_15px_rgba(139,92,246,0.5)]"
                : "bg-white/5 text-gray-500 border border-white/10"
            }`}
          >
            {currentStep > step.id ? (
              <Check className="w-5 h-5" />
            ) : (
              <step.icon className="w-5 h-5" />
            )}
          </div>
          {index < STEPS.length - 1 && (
            <div
              className={`w-12 h-0.5 mx-2 transition-all duration-300 ${
                currentStep > step.id ? "bg-violet-500" : "bg-white/10"
              }`}
            />
          )}
        </div>
      ))}
    </div>
  );

  // Render Step 1: Personal Info
  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-white mb-2">
          Personal Information
        </h3>
        <p className="text-gray-400 text-sm">
          Let's start with some basic info
        </p>
      </div>

      {/* Date of Birth */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Date of Birth <span className="text-red-400">*</span>
        </label>
        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <input
            type="text"
            value={formData.dateOfBirth}
            onChange={(e) => handleDateChange(e.target.value)}
            placeholder="MM/DD/YYYY"
            className={`w-full pl-10 pr-4 py-3 bg-[#080611] border rounded-[16px] text-white placeholder-gray-500 focus:outline-none focus:border-violet-500/50 transition-all shadow-[0_0_15px_rgba(127,92,246,0.15)_inset] hover:shadow-[0_0_20px_rgba(127,92,246,0.25)_inset] ${
              errors.dateOfBirth ? "border-red-500/70" : "border-[#7f5cff]/50"
            }`}
          />
        </div>
        {errors.dateOfBirth && (
          <p className="mt-1 text-sm text-red-400">{errors.dateOfBirth}</p>
        )}
      </div>

      {/* Country */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Country <span className="text-red-400">*</span>
        </label>
        <StyledSelect
          value={formData.country}
          onChange={(value) => updateField("country", value)}
          options={COUNTRIES.map((c) => ({ value: c, label: c }))}
          placeholder="Select country"
          variant="glow"
          icon={<MapPin className="w-5 h-5 text-gray-500" />}
        />
        {errors.country && (
          <p className="mt-1 text-sm text-red-400">{errors.country}</p>
        )}
      </div>

      {/* State (only for US) */}
      {formData.country === "United States" && (
        <div className="animate-fade-in">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            State
          </label>
          <StyledSelect
            value={formData.state}
            onChange={(value) => updateField("state", value)}
            options={US_STATES.map((s) => ({ value: s, label: s }))}
            placeholder="Select state (optional)"
            variant="glow"
          />
        </div>
      )}

      {/* Residency Status */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Residency Status <span className="text-red-400">*</span>
        </label>
        <StyledSelect
          value={formData.residencyStatus}
          onChange={(value) => updateField("residencyStatus", value)}
          options={RESIDENCY_STATUS.map((status) => ({
            value: status,
            label: status,
          }))}
          placeholder="Select status"
          variant="glow"
        />
        {errors.residencyStatus && (
          <p className="mt-1 text-sm text-red-400">{errors.residencyStatus}</p>
        )}
      </div>
    </div>
  );

  // Render Step 2: Academic Info
  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-white mb-2">
          Academic Information
        </h3>
        <p className="text-gray-400 text-sm">
          GPA is required. SAT, ACT, and major are optional - you can fill them
          later
        </p>
      </div>

      {/* GPA */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          GPA <span className="text-red-400">*</span>
        </label>
        <div className="relative group rounded-[16px] border-[1.2px] border-[#7f5cff]/50 bg-[#080611] transition-all duration-200 ease-out shadow-[0_0_15px_rgba(127,92,246,0.15)_inset] hover:shadow-[0_0_20px_rgba(127,92,246,0.25)_inset]">
          <input
            type="text"
            inputMode="decimal"
            value={formData.gpa}
            onChange={(e) => updateField("gpa", e.target.value)}
            placeholder="e.g., 3.85"
            className="w-full px-4 py-3 bg-transparent rounded-[16px] text-white placeholder-gray-500 focus:outline-none"
          />
        </div>
        {errors.gpa ? (
          <p className="mt-1 text-sm text-red-400">{errors.gpa}</p>
        ) : (
          <p className="mt-1 text-xs text-gray-500">
            Unweighted GPA on 4.0 scale
          </p>
        )}
      </div>

      {/* SAT Score */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          SAT Score
        </label>
        <div className="relative group rounded-[16px] border-[1.2px] border-[#7f5cff]/30 bg-[#080611] transition-all duration-200 ease-out shadow-[0_0_10px_rgba(127,92,246,0.1)_inset] hover:shadow-[0_0_15px_rgba(127,92,246,0.2)_inset]">
          <input
            type="text"
            inputMode="numeric"
            value={formData.satScore}
            onChange={(e) => updateField("satScore", e.target.value)}
            placeholder="e.g., 1450"
            className="w-full px-4 py-3 bg-transparent rounded-[16px] text-white placeholder-gray-500 focus:outline-none"
          />
        </div>
        {errors.satScore ? (
          <p className="mt-1 text-sm text-red-400">{errors.satScore}</p>
        ) : (
          <p className="mt-1 text-xs text-gray-500">Out of 1600</p>
        )}
      </div>

      {/* ACT Score */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          ACT Score
        </label>
        <div className="relative group rounded-[16px] border-[1.2px] border-[#7f5cff]/30 bg-[#080611] transition-all duration-200 ease-out shadow-[0_0_10px_rgba(127,92,246,0.1)_inset] hover:shadow-[0_0_15px_rgba(127,92,246,0.2)_inset]">
          <input
            type="text"
            inputMode="numeric"
            value={formData.actScore}
            onChange={(e) => updateField("actScore", e.target.value)}
            placeholder="e.g., 32"
            className="w-full px-4 py-3 bg-transparent rounded-[16px] text-white placeholder-gray-500 focus:outline-none"
          />
        </div>
        {errors.actScore ? (
          <p className="mt-1 text-sm text-red-400">{errors.actScore}</p>
        ) : (
          <p className="mt-1 text-xs text-gray-500">Out of 36</p>
        )}
      </div>

      {/* Intended Major */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Intended Major
        </label>
        <StyledSelect
          value={formData.intendedMajor}
          onChange={(value) => updateField("intendedMajor", value)}
          options={allMajors.map((major) => ({ value: major, label: major }))}
          placeholder="Select major (optional)"
          variant="glow"
        />
      </div>
    </div>
  );

  // Render Step 3: Review
  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-white mb-2">
          Review Your Information
        </h3>
        <p className="text-gray-400 text-sm">
          Make sure everything looks correct
        </p>
      </div>

      {/* Personal Information */}
      <div className="bg-white/5 rounded-xl p-4 border border-white/10">
        <h4 className="text-sm font-medium text-violet-400 mb-3 flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          Personal Information
        </h4>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-400">Date of Birth</span>
            <span className="text-white">{formData.dateOfBirth}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Country</span>
            <span className="text-white">{formData.country}</span>
          </div>
          {formData.country === "United States" && formData.state && (
            <div className="flex justify-between">
              <span className="text-gray-400">State</span>
              <span className="text-white">{formData.state}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-gray-400">Residency Status</span>
            <span className="text-white">{formData.residencyStatus}</span>
          </div>
        </div>
      </div>

      {/* Academic Information */}
      <div className="bg-white/5 rounded-xl p-4 border border-white/10">
        <h4 className="text-sm font-medium text-violet-400 mb-3 flex items-center gap-2">
          <GraduationCap className="w-4 h-4" />
          Academic Information
        </h4>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-400">GPA</span>
            <span className="text-white">{formData.gpa || "Not provided"}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">SAT Score</span>
            <span className="text-white">
              {formData.satScore || "Not provided"}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">ACT Score</span>
            <span className="text-white">
              {formData.actScore || "Not provided"}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Intended Major</span>
            <span className="text-white">
              {formData.intendedMajor || "Not provided"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-br from-gray-900/95 via-gray-900/98 to-black border border-white/10 rounded-2xl shadow-2xl shadow-violet-500/10 p-6 md:p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <h2 className="text-2xl md:text-3xl font-serif bg-gradient-to-r from-white via-violet-200 to-violet-400 bg-clip-text text-transparent">
              Complete Your Profile
            </h2>
            <p className="text-gray-400 text-sm mt-2">
              Step {currentStep} of {STEPS.length}
            </p>
          </div>

          {/* Progress */}
          {renderProgress()}

          {/* Content */}
          <div className="min-h-[300px]">
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/10">
            {/* Back Button */}
            {currentStep > 1 ? (
              <button
                onClick={handleBack}
                className="flex items-center gap-2 px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </button>
            ) : (
              <div />
            )}

            {/* Right Side Buttons */}
            <div className="flex items-center gap-3">
              {/* Skip Button (Step 2 only) */}
              {currentStep === 2 && (
                <button
                  onClick={handleSkip}
                  className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                >
                  Skip
                </button>
              )}

              {/* Next/Complete Button */}
              {currentStep < 3 ? (
                <button
                  onClick={handleNext}
                  className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white font-medium rounded-xl shadow-lg shadow-violet-500/25 transition-all hover:shadow-violet-500/40"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={handleComplete}
                  disabled={isSubmitting}
                  className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white font-medium rounded-xl shadow-lg shadow-violet-500/25 transition-all hover:shadow-violet-500/40 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Completing...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Complete Profile
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfileCompletionModal;
